import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Cart drawer component — matches original Savor pattern with minimal additions.
 *
 * This component ONLY manages:
 *  1. Auto-opening the drawer when a product is added (CartAddEvent)
 *  2. Updating sticky summary state when the dialog opens
 *  3. Dispatching recovercann:refresh so the validation summary initialises
 *
 * Cart content is populated entirely by cart-items-component's own #handleCartUpdate
 * listener, which receives section HTML from the CartAddEvent dispatched by
 * product-form-component.  This component does NOT fetch sections, morph DOM,
 * or dispatch cart:update.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number} */
  #lastRefreshAt = 0;

  /** @type {number} */
  #refreshMinIntervalMs = 5000;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open') && !window.matchMedia('(max-width: 749px)').matches) {
      this.showDialog();
    }
  };

  #onDialogOpen = () => {
    this.#updateStickyState();

    const now = Date.now();
    const shouldRefresh = (now - this.#lastRefreshAt) >= this.#refreshMinIntervalMs;
    if (shouldRefresh) {
      this.#lastRefreshAt = now;
    }

    // Dispatch recovercann:refresh after the current microtask queue settles
    // so that cart-items-component has finished its morph (and the validation
    // container is present in the DOM) before the validator tries to refresh.
    if (shouldRefresh) {
      queueMicrotask(() => {
        document.dispatchEvent(new CustomEvent('recovercann:refresh', {
          detail: {
            sourceId: 'cart-drawer-open',
            forceRefresh: true,
            silentRefresh: true,
            data: { source: 'cart-drawer-open' },
          },
        }));
      });
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {{ dialog: HTMLDialogElement }} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
